import type * as React from 'react'
import { useState } from 'react'

import { cn } from '../../lib/utils'
import { LOGO_WRITE_DURATION_MS } from './logo-loader-geometry'
import { MarkPair, Wordmark, WrittenMark } from './logo-loader-parts'

const SIZES = { sm: 22, lg: 64 } as const

/**
 * The Multaqa mark being written on, as one continuous pen stroke (P-26).
 *
 * The mark starts completely invisible. One copy of it sits inside an SVG,
 * masked by a single stroked path, WRITE_PATH. `animate-logo-write-stroke`,
 * registered in @multaqa/tokens theme.css beside the accordion pair, pulls
 * that stroke's `stroke-dashoffset` from its own length down to 0. The mask
 * therefore uncovers the mark in writing order - bottom-left foot, apex, vee,
 * right apex, bottom-right foot - and the M appears as a hand draws it, left
 * to right, with no pen lift and no backward jump.
 *
 * The markup is in logo-loader-parts.tsx; the numbers it draws with are in
 * logo-loader-geometry.ts.
 *
 * What makes it read as hand-made is the timing, not the path. A hand slows to
 * about a quarter of its cruising speed at every reversal and accelerates out
 * again, so each of the four segments carries its own curve. See the
 * `logo-write-stroke` keyframes.
 *
 * There is NO camera. An earlier version scaled the whole loader up until the
 * mark filled the height of the screen, then pulled back while the rest was
 * drawn. It was removed. The loader now writes itself at its resting size,
 * wherever the surrounding page has centred it, which is also why no container
 * has to clip its overflow for it any more.
 *
 * The full-screen size then sweeps the wordmark in out of the gap beside the
 * mark, and the finished logo holds still for a moment, so the screen never
 * cuts away at the instant the mark lands. The whole sequence is 1.5 seconds:
 * 800ms of writing, a 340ms sweep, then a 360ms hold.
 *
 * It writes once per mount and holds. There is no loop and no unwrite, so a
 * screen can never replace a half-erased mark.
 *
 * The reveal is a mask, deliberately: the PNG carries a fixed brand red at its
 * centre, and a filter flattens that to one colour. Masking an `<image>`
 * inside SVG also works everywhere, while a CSS `mask-image` pointing at an
 * SVG `<mask>` does not. The copy swaps files with the same `dark:` pair
 * `<Logo>` uses, so the loader tracks the theme with no extra asset.
 *
 * Under `prefers-reduced-motion: reduce` the branch renders the mark once,
 * static at 60% opacity, with no animated element at all. No spinner stands in
 * - motion is the problem, not this shape.
 */
function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  )
}

function LogoLoader({
  className,
  size = 'sm',
  label,
  ...props
}: React.ComponentProps<'div'> & { size?: keyof typeof SIZES; label?: string }) {
  // Read once per mount. A loader mounts fresh for every pending state, so a
  // preference flipped mid-session is picked up on the next one.
  const [reducedMotion] = useState(prefersReducedMotion)
  const px = SIZES[size]

  const mark = reducedMotion ? (
    <span aria-hidden="true" className="block opacity-60">
      <MarkPair size={px} />
    </span>
  ) : (
    <span aria-hidden="true" className="relative inline-block">
      <WrittenMark size={px} />
    </span>
  )

  const content =
    size === 'lg' ? (
      <>
        {mark}
        <Wordmark animated={!reducedMotion} />
      </>
    ) : (
      mark
    )

  const rootClass = cn('text-foreground inline-flex items-center gap-3', className)

  // Two returns rather than one element with a conditional `role`, for the
  // reason Spinner documents: `aria-label` is only valid once the div has a
  // role, and the unlabelled case must stay out of the accessibility tree
  // entirely - a loader inside ScreenSkeleton would otherwise double-announce
  // a region that already says the screen is loading.
  if (label) {
    return (
      <div
        data-slot="logo-loader"
        data-size={size}
        role="status"
        aria-label={label}
        className={rootClass}
        {...props}
      >
        {content}
      </div>
    )
  }

  return (
    <div
      data-slot="logo-loader"
      data-size={size}
      aria-hidden="true"
      className={rootClass}
      {...props}
    >
      {content}
    </div>
  )
}

export { LOGO_WRITE_DURATION_MS, LogoLoader }
